import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { motion, AnimatePresence } from "framer-motion";
import useSeo from "@/lib/useSeo";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { Download, Printer, X } from "lucide-react";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Legend,
  Tooltip,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
} from "recharts";

// A distinctive but restrained palette for up to 8 directorates
const PALETTE = ["#E86A20", "#0E2148", "#6D82B4", "#B23A1A", "#3B4A6B", "#8A6E3E", "#2E7D6B", "#7A4E9A"];

export default function Directorates() {
  const { code } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState(null);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const reportRef = useRef(null);

  useSeo({
    title: `Directorates · ${code} — Langwood360`,
    description: "Cross-Directorate comparison across a board and its direct reports.",
    path: `/board/${code}/directorates`,
    noindex: true,
  });

  useEffect(() => {
    api
      .get(`/board-sessions/${code}/directorates`, { params: { token } })
      .then(({ data }) => setData(data))
      .catch((e) => setError(e.response?.data?.detail || e.message));
    api
      .get(`/board-sessions/${code}/directorates/trend`, { params: { token } })
      .then(({ data }) => setTrend(data))
      .catch(() => {});
  }, [code, token]);

  const downloadPdf = async () => {
    if (!reportRef.current || downloading) return;
    setDownloading(true);
    reportRef.current.classList.add("pdf-capture");
    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        backgroundColor: "#F4EEDC",
        useCORS: true,
        windowWidth: reportRef.current.scrollWidth,
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const imgW = pageW - 48;
      const imgH = (canvas.height / canvas.width) * imgW;
      // Paginate: slice the tall canvas into A4 pages
      let heightLeft = imgH;
      let position = 24;
      pdf.addImage(imgData, "PNG", 24, position, imgW, imgH);
      heightLeft -= pageH - 24;
      while (heightLeft > 0) {
        pdf.addPage();
        position = 24 - (imgH - heightLeft);
        pdf.addImage(imgData, "PNG", 24, position, imgW, imgH);
        heightLeft -= pageH;
      }
      const orgSlug = (data?.organisation || "report").replace(/\s+/g, "-");
      pdf.save(`Cross-Directorate-${orgSlug}-${code}.pdf`);
    } finally {
      reportRef.current?.classList.remove("pdf-capture");
      setDownloading(false);
    }
  };

  const printReport = () => window.print();

  if (error) {
    return (
      <div className="min-h-screen bg-linen">
        <Navbar />
        <div className="max-w-4xl mx-auto p-12 text-heritage" data-testid="directorates-error">
          {error}
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-linen">
        <Navbar />
        <div className="max-w-4xl mx-auto p-12 tick-label">Loading…</div>
      </div>
    );
  }

  const { directorates, dimension_matrix, summary, organisation, facilitator_name } = data;
  const withData = directorates.filter((d) => d.blended_vri != null);
  const hasData = withData.length > 0;

  return (
    <div className="min-h-screen bg-linen">
      <Navbar />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-16 pb-8">
        <div className="tick-label-accent mb-4">Board Session · {code} · Cross-Directorate Report</div>
        <h1 className="display-serif text-4xl md:text-6xl" data-testid="directorates-title">
          How each <em className="italic text-orange">directorate</em> sees the organisation.
        </h1>
        <div className="tick-label mt-3">
          {organisation} · Facilitated by {facilitator_name}
        </div>
        <p className="mt-6 max-w-2xl text-lg text-charcoal leading-relaxed">
          Every board director and their direct reports form a distinct <em>directorate</em> — a lens
          through which the organisation is being read. Compared side by side, the silos reveal which
          part of the business is realising value, and which is quietly drifting.
        </p>
        <div className="rule-orange my-8" />
        <div className="flex gap-4 flex-wrap">
          <Link to={`/board/${code}/results?token=${token}`} className="btn-ghost" data-testid="back-to-board-results">
            ← Back to Board Results
          </Link>
          <button
            onClick={downloadPdf}
            disabled={downloading || !data}
            className="btn-ghost"
            data-testid="directorates-download-pdf"
          >
            <Download className="w-3.5 h-3.5" strokeWidth={1.5} />
            {downloading ? "Preparing PDF…" : "Download PDF"}
          </button>
          <button
            onClick={printReport}
            disabled={!data}
            className="btn-ghost"
            data-testid="directorates-print"
          >
            <Printer className="w-3.5 h-3.5" strokeWidth={1.5} />
            Print
          </button>
        </div>
      </section>

      <div ref={reportRef} className="bg-linen">
        <PrintHeader data={data} code={code} />

      {!hasData && (
        <section className="max-w-4xl mx-auto px-6 lg:px-12 py-12">
          <div className="border border-oxford/15 bg-linen p-10 text-center">
            <div className="tick-label-accent mb-4">No directorate data yet</div>
            <p className="text-charcoal">
              Add board members from the Member Hub and invite their direct reports. Once each
              directorate has at least one respondent, this comparison will populate automatically.
            </p>
          </div>
        </section>
      )}

      {hasData && summary && (
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-8" data-testid="directorates-summary">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-px bg-oxford/10">
            <SummaryCell
              label="Directorates"
              value={summary.directorate_count}
              sub={`${summary.with_data_count} with data`}
              testid="summary-count"
            />
            <SummaryCell
              label="Strongest"
              value={summary.strongest.blended_vri}
              sub={summary.strongest.label}
              accent
              testid="summary-strongest"
            />
            <SummaryCell
              label="Spread"
              value={summary.spread}
              sub="VRI points between top and bottom"
              testid="summary-spread"
            />
            <SummaryCell
              label="Flagged"
              value={summary.flagged_count}
              sub="directorates drifting or disconnected"
              testid="summary-flagged"
            />
          </div>
          {summary.biggest_gap && (
            <div className="mt-6 border border-orange bg-parchment p-6" data-testid="biggest-gap-flag">
              <div className="tick-label-accent text-orange">The Widest Gap</div>
              <div className="display-serif text-2xl mt-2">
                {summary.biggest_gap.label}
              </div>
              <p className="text-charcoal mt-2">
                A <span className="font-mono">{summary.biggest_gap.cascade_gap}-point</span> gap
                between director and team — currently classified as{" "}
                <em>{summary.biggest_gap.cascade_label}</em>. Start the conversation here.
              </p>
            </div>
          )}
        </section>
      )}

      {/* Ranking / bar comparison */}
      {hasData && (
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-8" data-testid="directorate-ranking">
          <div className="tick-label-accent mb-6">Directorates, ranked by blended VRI</div>
          <div className="border border-oxford/15 bg-linen divide-y divide-oxford/10">
            {directorates.map((d, i) => (
              <DirectorateRow key={d.member_id} d={d} idx={i} colour={PALETTE[i % PALETTE.length]} />
            ))}
          </div>
        </section>
      )}

      {/* Radar overlay */}
      {hasData && (
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
          <div className="border border-oxford/15 bg-linen p-8" data-testid="directorate-radar">
            <div className="tick-label-accent mb-4">Dimension profiles</div>
            <p className="text-sm text-charcoal mb-6 max-w-2xl">
              Each shape below is one directorate's blended read of the five disciplines. Where the
              shapes diverge, the silos are describing different organisations.
            </p>
            <ResponsiveContainer width="100%" height={480}>
              <RadarChart data={dimension_matrix} outerRadius="70%">
                <PolarGrid gridType="polygon" stroke="#0E2148" strokeOpacity={0.18} />
                <PolarAngleAxis dataKey="dimension" tick={{ fill: "#0E2148", fontSize: 11 }} />
                <PolarRadiusAxis domain={[0, 100]} tick={{ fill: "#6D82B4", fontSize: 10 }} axisLine={false} />
                {directorates.map((d, i) => (
                  <Radar
                    key={d.member_id}
                    name={d.label}
                    dataKey={d.label}
                    stroke={PALETTE[i % PALETTE.length]}
                    fill={PALETTE[i % PALETTE.length]}
                    fillOpacity={0.14}
                    strokeWidth={1.8}
                  />
                ))}
                <Tooltip
                  contentStyle={{ background: "#FAF5EA", border: "1px solid #0E214830", fontSize: 12 }}
                />
                <Legend
                  wrapperStyle={{ fontSize: 12, color: "#0E2148" }}
                  iconType="circle"
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}

      {/* Historical trend */}
      {hasData && trend && trend.directorates && trend.directorates.length > 0 && trend.weeks && trend.weeks.length > 0 && (
        <DirectorateTrend trend={trend} />
      )}

      {/* Dimension matrix table */}
      {hasData && (
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-8" data-testid="directorate-matrix">
          <div className="tick-label-accent mb-6">Dimension score matrix</div>
          <div className="overflow-x-auto border border-oxford/15 bg-linen">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-oxford/15 bg-parchment">
                  <th className="text-left px-4 py-3 tick-label">Dimension</th>
                  {directorates.map((d, i) => (
                    <th key={d.member_id} className="text-right px-4 py-3 tick-label" style={{ color: PALETTE[i % PALETTE.length] }}>
                      {d.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dimension_matrix.map((row) => (
                  <tr key={row.dimension} className="border-b border-oxford/10 last:border-0">
                    <td className="px-4 py-3 display-serif text-lg text-oxford">{row.dimension}</td>
                    {directorates.map((d) => (
                      <td key={d.member_id} className="px-4 py-3 text-right font-mono text-oxford">
                        {row[d.label] == null ? "—" : `${row[d.label]}%`}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="rule mb-8" />
        <div className="flex gap-4">
          <Link to={`/board/${code}/results?token=${token}`} className="btn-ghost">
            ← Back to Board Results
          </Link>
        </div>
      </section>
      </div>
      <Footer />
    </div>
  );
}

function PrintHeader({ data, code }) {
  if (!data) return null;
  const today = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-4 pb-2 print-only-header" data-testid="print-header">
      <div className="flex items-baseline justify-between border-b border-oxford/15 pb-4">
        <div className="tick-label-accent">Value Realisation Index™ · Cross-Directorate Report</div>
        <div className="tick-label">{today} · {data.organisation} · {code}</div>
      </div>
    </section>
  );
}

function SummaryCell({ label, value, sub, accent, testid }) {
  return (
    <div className="bg-linen p-8" data-testid={testid}>
      <div className={accent ? "tick-label-accent" : "tick-label"}>{label}</div>
      <div className="mt-3 font-mono text-5xl text-oxford leading-none">{value ?? "—"}</div>
      {sub && <div className="mt-2 tick-label">{sub}</div>}
    </div>
  );
}

function DirectorateRow({ d, idx, colour }) {
  const labelColour =
    d.cascade_label === "Aligned"
      ? "text-orange"
      : d.cascade_label === "Divergent"
      ? "text-slate"
      : d.cascade_label === "Disconnected"
      ? "text-heritage"
      : "text-charcoal";
  const barWidth = d.blended_vri != null ? Math.min(100, (d.blended_vri / 125) * 100) : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: idx * 0.05, ease: [0.22, 1, 0.36, 1] }}
      className="p-6"
      data-testid={`directorate-row-${idx}`}
    >
      <div className="flex items-baseline justify-between flex-wrap gap-4">
        <div className="flex items-baseline gap-4">
          <div className="font-mono text-2xl text-oxford/40 w-8">
            {d.rank != null ? String(d.rank).padStart(2, "0") : "—"}
          </div>
          <div>
            <div className="display-serif text-2xl text-oxford" style={{ color: colour }}>
              {d.name}
            </div>
            <div className="tick-label mt-1">
              {d.role || "Director"} · {d.participant_count} respondent{d.participant_count === 1 ? "" : "s"}
              {" · "}
              {d.team_count} direct report{d.team_count === 1 ? "" : "s"}
            </div>
          </div>
        </div>
        <div className="flex items-baseline gap-6">
          <div className="text-right">
            <div className="tick-label">Blended VRI</div>
            <div className="font-mono text-4xl text-oxford mt-1" data-testid={`directorate-blended-${idx}`}>
              {d.blended_vri ?? "—"}
            </div>
          </div>
          <div className="text-right">
            <div className="tick-label">Cascade Gap</div>
            <div
              className={`font-mono text-4xl mt-1 ${labelColour}`}
              data-testid={`directorate-gap-${idx}`}
            >
              {d.cascade_gap ?? "—"}
            </div>
          </div>
          <div className="text-right min-w-[110px]">
            <div className="tick-label">Status</div>
            <div className={`tick-label-accent mt-1 ${labelColour}`} data-testid={`directorate-status-${idx}`}>
              {d.flagged && "⚑ "}
              {d.cascade_label}
            </div>
          </div>
        </div>
      </div>

      {d.blended_vri != null && (
        <div className="mt-6">
          <div className="relative h-2 bg-oxford/10 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: `${barWidth}%` }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.1 + idx * 0.05 }}
              className="absolute top-0 left-0 h-full"
              style={{ background: colour }}
            />
          </div>
          <div className="flex justify-between mt-2">
            <span className="tick-label">0</span>
            <span className="tick-label">Director {d.own_vri ?? "—"} · Team {d.team_avg_vri ?? "—"}</span>
            <span className="tick-label">125</span>
          </div>
        </div>
      )}
    </motion.div>
  );
}


function bandColour(label) {
  if (label === "Aligned") return "#E86A20";
  // Darker slate for AA contrast on linen (#F4EEDC).
  if (label === "Divergent") return "#3B4A6B";
  if (label === "Disconnected") return "#B23A1A";
  return "#0E214830";
}

function DirectorateTrend({ trend }) {
  const { weeks, directorates } = trend;
  const withChanges = directorates.filter((d) => d.band_change).length;
  const [selected, setSelected] = useState(null);

  return (
    <section
      className="border-t border-oxford/10 bg-parchment mt-4"
      data-testid="directorate-trend"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="tick-label-accent mb-4">A Fourth Dimension · Time</div>
        <h2 className="display-serif text-3xl md:text-5xl leading-tight max-w-3xl">
          How each directorate is <em className="italic text-orange">moving</em>.
        </h2>
        <div className="rule-orange my-6" />
        <p className="max-w-2xl text-charcoal leading-relaxed">
          A single snapshot names the problem. A trend names the <em>direction</em>. Below is the
          weekly band history for each directorate — a moving read on which parts of the business
          are closing the gap, and which are quietly opening it.
        </p>
        <div className="tick-label mt-4">
          {weeks.length} week{weeks.length === 1 ? "" : "s"} of history ·{" "}
          {withChanges} directorate{withChanges === 1 ? "" : "s"} changed band ·{" "}
          <span className="text-orange">click a row for the week-by-week detail</span>
        </div>

        <div className="mt-10 space-y-6">
          {directorates.map((d, idx) => (
            <TrendRow
              key={d.member_id}
              d={d}
              weeks={weeks}
              idx={idx}
              onOpen={() => setSelected(d)}
            />
          ))}
        </div>

        {/* Legend */}
        <div className="mt-10 flex flex-wrap gap-6 text-sm border-t border-oxford/10 pt-6">
          <LegendChip colour="#E86A20" label="Aligned" desc="Gap ≤ 8" />
          <LegendChip colour="#6D82B4" label="Divergent" desc="9–20" />
          <LegendChip colour="#B23A1A" label="Disconnected" desc="> 20" />
        </div>
      </div>

      {selected && (
        <TrendDetailModal
          directorate={selected}
          weeks={weeks}
          onClose={() => setSelected(null)}
        />
      )}
    </section>
  );
}

function LegendChip({ colour, label, desc }) {
  return (
    <div className="flex items-center gap-2">
      <span className="inline-block w-3 h-3" style={{ background: colour }} />
      <span className="text-oxford">{label}</span>
      <span className="tick-label">{desc}</span>
    </div>
  );
}

function TrendRow({ d, weeks, idx, onOpen }) {
  const bandChange = d.band_change;
  const deltaVri = d.delta_vri;
  const arrow = deltaVri == null ? null : deltaVri > 0 ? "▲" : deltaVri < 0 ? "▼" : "·";
  const deltaColour =
    deltaVri == null ? "text-charcoal" : deltaVri > 0 ? "text-orange" : deltaVri < 0 ? "text-heritage" : "text-charcoal";

  return (
    <motion.button
      type="button"
      onClick={onOpen}
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: idx * 0.06 }}
      whileHover={{ borderColor: "#E86A20" }}
      className="w-full text-left border border-oxford/15 bg-linen p-6 hover:bg-parchment/60 focus:outline-none focus:ring-2 focus:ring-orange transition-colors cursor-pointer"
      data-testid={`trend-row-${idx}`}
      aria-label={`Open weekly detail for ${d.name}`}
    >
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <div className="display-serif text-xl text-oxford">{d.name}</div>
          <div className="tick-label mt-0.5">{d.role || "Director"}</div>
        </div>
        <div className="flex items-baseline gap-6">
          {bandChange && (
            <div className="text-right" data-testid={`trend-band-change-${idx}`}>
              <div className="tick-label">Band</div>
              <div className="mt-1 font-mono text-sm">
                <span style={{ color: bandColour(bandChange.from) }}>{bandChange.from}</span>
                <span className="text-oxford/40 mx-2">→</span>
                <span style={{ color: bandColour(bandChange.to) }}>{bandChange.to}</span>
              </div>
            </div>
          )}
          <div className="text-right">
            <div className="tick-label">Δ VRI</div>
            <div className={`font-mono text-2xl mt-1 ${deltaColour}`} data-testid={`trend-delta-${idx}`}>
              {deltaVri == null ? "—" : `${arrow} ${Math.abs(deltaVri)}`}
            </div>
          </div>
          <div className="text-right">
            <div className="tick-label">Now</div>
            <div className="font-mono text-2xl text-oxford mt-1">
              {d.current ? d.current.blended_vri : "—"}
            </div>
          </div>
        </div>
      </div>

      {/* Timeline chips */}
      <div className="mt-5 flex gap-1.5 items-end" data-testid={`trend-timeline-${idx}`}>
        {weeks.map((wk, i) => {
          const pt = d.series[i];
          const colour = pt ? bandColour(pt.cascade_label) : "#0E214810";
          const height = pt ? Math.max(14, Math.min(48, (pt.blended_vri / 125) * 48)) : 14;
          return (
            <div key={wk} className="flex flex-col items-center flex-1 min-w-0">
              <div
                title={pt ? `${wk} · VRI ${pt.blended_vri} · ${pt.cascade_label}` : `${wk} · no data`}
                className="w-full transition-all"
                style={{ background: colour, height: `${height}px` }}
              />
              <div className="tick-label mt-2 text-[9px] truncate w-full text-center">
                {wk.replace(/^\d{4}-/, "")}
              </div>
            </div>
          );
        })}
      </div>
    </motion.button>
  );
}

function TrendDetailModal({ directorate, weeks, onClose }) {
  const d = directorate;
  // Weekly VRI + gap line chart
  const chartData = weeks.map((wk, i) => {
    const pt = d.series[i];
    return {
      week: wk.replace(/^\d{4}-/, ""),
      vri: pt ? pt.blended_vri : null,
      gap: pt ? pt.cascade_gap : null,
      label: pt ? pt.cascade_label : null,
    };
  });

  // Dimensions matrix — union of all dimension names, weeks as columns
  const dimNames = [];
  d.series.forEach((pt) => {
    if (pt?.dimensions) {
      pt.dimensions.forEach((dd) => {
        if (!dimNames.includes(dd.dimension)) dimNames.push(dd.dimension);
      });
    }
  });

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 z-50 bg-oxford/60 backdrop-blur-sm flex items-start justify-center p-4 md:p-10 overflow-y-auto"
        onClick={onClose}
        data-testid="trend-detail-modal"
      >
        <motion.div
          initial={{ y: 24, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 24, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-5xl bg-linen border border-oxford/20 shadow-2xl my-8"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-start justify-between p-6 md:p-10 border-b border-oxford/10">
            <div>
              <div className="tick-label-accent mb-2">Weekly detail · Directorate</div>
              <div className="display-serif text-3xl md:text-4xl text-oxford">{d.name}</div>
              <div className="tick-label mt-2">{d.role || "Director"}</div>
            </div>
            <button
              onClick={onClose}
              className="btn-ghost"
              data-testid="trend-modal-close"
              aria-label="Close weekly detail"
            >
              <X className="w-4 h-4" strokeWidth={1.5} />
              Close
            </button>
          </div>

          {/* Line chart */}
          <div className="p-6 md:p-10 border-b border-oxford/10">
            <div className="tick-label-accent mb-4">Blended VRI · Cascade Gap</div>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={chartData} margin={{ top: 8, right: 24, left: 0, bottom: 8 }}>
                <CartesianGrid stroke="#0E2148" strokeOpacity={0.08} vertical={false} />
                <XAxis dataKey="week" tick={{ fill: "#0E2148", fontSize: 11 }} />
                <YAxis yAxisId="left" domain={[0, 125]} tick={{ fill: "#0E2148", fontSize: 10 }} />
                <YAxis yAxisId="right" orientation="right" domain={[0, 60]} tick={{ fill: "#B23A1A", fontSize: 10 }} />
                <Tooltip
                  contentStyle={{ background: "#FAF5EA", border: "1px solid #0E214830", fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 12, color: "#0E2148" }} iconType="circle" />
                <Line yAxisId="left" type="monotone" name="Blended VRI" dataKey="vri" stroke="#0E2148" strokeWidth={2.5} dot={{ r: 4, stroke: "#0E2148", strokeWidth: 2, fill: "#F4EEDC" }} activeDot={{ r: 6, fill: "#0E2148" }} isAnimationActive={false} connectNulls />
                <Line yAxisId="right" type="monotone" name="Cascade Gap" dataKey="gap" stroke="#B23A1A" strokeWidth={2.5} dot={{ r: 4, stroke: "#B23A1A", strokeWidth: 2, fill: "#F4EEDC" }} activeDot={{ r: 6, fill: "#B23A1A" }} isAnimationActive={false} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Dimensions matrix */}
          {dimNames.length > 0 && (
            <div className="p-6 md:p-10" data-testid="trend-modal-dimensions">
              <div className="tick-label-accent mb-4">Dimensions, week by week</div>
              <div className="overflow-x-auto -mx-6 md:-mx-10 px-6 md:px-10">
                <table className="w-full text-sm min-w-[640px]">
                  <thead>
                    <tr className="border-b border-oxford/15 bg-parchment">
                      <th className="text-left px-3 py-2 tick-label">Week</th>
                      <th className="text-right px-3 py-2 tick-label">VRI</th>
                      <th className="text-right px-3 py-2 tick-label">Gap</th>
                      <th className="text-right px-3 py-2 tick-label">Band</th>
                      {dimNames.map((n) => (
                        <th key={n} className="text-right px-3 py-2 tick-label">{n.split(" ")[0]}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {weeks.map((wk, i) => {
                      const pt = d.series[i];
                      if (!pt) {
                        return (
                          <tr key={wk} className="border-b border-oxford/10 opacity-40">
                            <td className="px-3 py-2 font-mono text-xs">{wk}</td>
                            <td colSpan={3 + dimNames.length} className="px-3 py-2 text-charcoal italic">no snapshot</td>
                          </tr>
                        );
                      }
                      const bColour = bandColour(pt.cascade_label);
                      return (
                        <tr key={wk} className="border-b border-oxford/10">
                          <td className="px-3 py-2 font-mono text-xs">{wk}</td>
                          <td className="px-3 py-2 text-right font-mono text-oxford">{pt.blended_vri}</td>
                          <td className="px-3 py-2 text-right font-mono text-heritage">{pt.cascade_gap}</td>
                          <td className="px-3 py-2 text-right font-mono text-xs" style={{ color: bColour }}>
                            {pt.cascade_label}
                          </td>
                          {dimNames.map((n) => {
                            const dd = (pt.dimensions || []).find((x) => x.dimension === n);
                            return (
                              <td key={n} className="px-3 py-2 text-right font-mono text-oxford">
                                {dd ? `${dd.score}%` : "—"}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
